/** The three fields any container shape carries that can name it. */
interface ContainerLabelParts {
  is_test?: boolean | null;
  container_number?: string | null;
  entity_name?: string | null;
}

/**
 * What to call a container on screen.
 *
 * A real container is its registration's container number; `entity_name` is the
 * fallback for one whose registration row carries no number.
 *
 * A TEST container inverts that. It is backed by a throwaway registration whose
 * container number is a generated `OCR-TEST-<timestamp>-<rand>` stamp, so the
 * generic rule named every ad-hoc test container after the machinery that made
 * it instead of the file the operator uploaded (OMA-632). `entity_name` holds
 * that file name — read off the upload and sanitised server-side — so it wins,
 * and the `OCR-TEST-…` stamp is never shown.
 *
 * `untitledTestLabel` is the translated placeholder for a test container whose
 * upload carried no usable name; pass `t('untitledTestDocument')`. It lives
 * here as a parameter rather than a hardcoded string so this stays a pure
 * function and the caller owns the locale.
 *
 * One function, because the rule is duplicated the moment it is written twice —
 * and a container named two ways on two screens is how "which one was mine?"
 * starts.
 */
export function resolveOcrContainerLabel(
  container: ContainerLabelParts | null | undefined,
  untitledTestLabel: string
): string {
  if (container?.is_test) {
    return container.entity_name?.trim() || untitledTestLabel;
  }

  return container?.container_number ?? container?.entity_name ?? '-';
}

/**
 * The id shown before a container's name — `55- REG-2026-167` (OMA-670).
 *
 * A test container is labelled with the file the operator uploaded, so two
 * uploads of the same file looked identical. Every container, real or test,
 * therefore leads with its own record id: unique, already on every container
 * payload, and short enough to read at a glance. Null when there is no id;
 * the caller then renders nothing.
 */
export function resolveOcrContainerIdPrefix(
  id: number | null | undefined
): string | null {
  return id ? String(id) : null;
}
